// Article body: the project's long description with auto-placed figures.
//
// Layout: an editorial CSS grid with a centered reading column. Figures are
// block-level breakouts that alternate between extending into the left and
// right margin — no floats, so prose never wraps raggedly around an image.
// Each figure carries a mono "FIG. NN" label and its caption in the column
// beside the image, like a technical dossier.
//
// Text blocks support a small markdown subset: headings, lists, and inline
// bold / italic / code / links (see utils/parseArticle, utils/inlineMarkdown).
import React from 'react';
import { motion } from 'framer-motion';
import { parseInline } from '../../../utils/inlineMarkdown';

const FIGURE_REVEAL = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px 0px' },
  transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] },
};

// Renders inline-markdown tokens. Plain text stays a bare string node.
function Inline({ text }) {
  return parseInline(text).map((token, i) => {
    switch (token.type) {
      case 'code':
        return <code key={i}>{token.text}</code>;
      case 'bold':
        return <strong key={i}>{token.text}</strong>;
      case 'italic':
        return <em key={i}>{token.text}</em>;
      case 'link':
        return (
          <a
            key={i}
            href={token.href}
            target="_blank"
            rel="noopener noreferrer"
            className="pd-article__link"
          >
            {token.text}
          </a>
        );
      default:
        return <React.Fragment key={i}>{token.text}</React.Fragment>;
    }
  });
}

function Figure({ image, side, number, onOpen }) {
  return (
    <motion.figure className={`pd-figure pd-figure--${side}`} {...FIGURE_REVEAL}>
      <button
        type="button"
        className="pd-figure__frame"
        onClick={() => onOpen(image)}
        aria-label={image.caption ? `Open ${image.caption}` : `Open figure ${number}`}
      >
        <img
          src={image.thumbnailSrc || image.src}
          alt={image.caption || ''}
          loading="lazy"
          decoding="async"
          className="pd-figure__img"
        />
      </button>
      <figcaption className="pd-figure__meta">
        <span className="pd-figure__num">Fig. {number}</span>
        {image.caption && (
          <span className="pd-figure__caption">{image.caption}</span>
        )}
      </figcaption>
    </motion.figure>
  );
}

export default function ProjectArticle({ summary, blocks, onOpenImage }) {
  // Figures alternate sides by their order of appearance, not by index in
  // the image array, so the rhythm stays right even when images are skipped.
  let figureOrdinal = 0;

  return (
    <article className="pd-article">
      {summary && <p className="pd-article__lede">{summary}</p>}
      <div className="pd-article__body">
        {blocks.map((block, i) => {
          if (block.type === 'paragraph') {
            return (
              <p key={`p-${i}`} className="pd-article__p">
                <Inline text={block.text} />
              </p>
            );
          }
          if (block.type === 'heading') {
            const Tag = `h${Math.min(block.level, 4)}`;
            return (
              <Tag key={`h-${i}`} className="pd-article__h">
                <Inline text={block.text} />
              </Tag>
            );
          }
          if (block.type === 'list') {
            const Tag = block.ordered ? 'ol' : 'ul';
            return (
              <Tag key={`l-${i}`} className="pd-article__list">
                {block.items.map((item, j) => (
                  <li key={j}>
                    <Inline text={item} />
                  </li>
                ))}
              </Tag>
            );
          }
          if (block.type === 'figure') {
            figureOrdinal += 1;
            return (
              <Figure
                key={`f-${block.image.index}`}
                image={block.image}
                side={figureOrdinal % 2 === 1 ? 'right' : 'left'}
                number={String(figureOrdinal).padStart(2, '0')}
                onOpen={onOpenImage}
              />
            );
          }
          return null;
        })}
      </div>
    </article>
  );
}
